import { injectable } from 'inversify';
import type DebatDirectTypings from '@debatdirect/common/types/debatdirect-typings';
import { socketServiceConfig } from '@debatdirect/common/src/constants';
import env from '@debatdirect/common/src/env';
import type { Structure } from '@debatdirect/common/types/immstruct';
import { container } from '@debatdirect/common/src/modules/container';
import { logError } from '@debatdirect/common/src/logger';

import LiveClientWorker from './worker?worker';

export interface SocketObserver {
  onAgendaUpdateEvent: (eventData: DebatDirectTypings.IOAgendaUpdateEvent['agenda']['debates']) => void;
  onAgendaDeleteEvent: (eventData: DebatDirectTypings.IOAgendaUpdateDeleteEvent['agenda']['debates']) => void;
  onNotificationUpdateEvent: (eventData: DebatDirectTypings.IOLiveNotificationEvent) => void;
  onVotingRoundEvent: (eventData: DebatDirectTypings.IOVotingRoundEvent) => void;
  onDebateEvent: (eventData: DebatDirectTypings.IODebateEvent) => void;
}

type AgendaUpdateEvent = MessageEvent<DebatDirectTypings.IOAgendaUpdateEvent>;
type AgendaDeleteEvent = MessageEvent<DebatDirectTypings.IOAgendaUpdateDeleteEvent>;
type DebateEvent = MessageEvent<DebatDirectTypings.IODebateEvent>;
type LiveNotificationEvent = MessageEvent<DebatDirectTypings.IOLiveNotificationEvent>;
type VotingRoundEvent = MessageEvent<DebatDirectTypings.IOVotingRoundEvent>;

type WorkerEvent = MessageEvent | AgendaDeleteEvent | AgendaUpdateEvent | DebateEvent | LiveNotificationEvent | VotingRoundEvent;

@injectable()
class SocketService {
  connected = false;
  debateId: string | null = null;
  worker?: Worker;
  observer?: SocketObserver;
  eventTimeout: number;
  listener: (event: WorkerEvent) => void;

  constructor() {
    this.eventTimeout = socketServiceConfig.eventTimeout;
    this.listener = (event) => this.handleEvent(event);

    // restart the worker when the connection is restored
    window.addEventListener('online', () => {
      this.start();
    });

    if (env.DEV) {
      // @ts-ignore
      window.testLiveEvent = (event: WorkerEvent) => {
        this.handleEvent(event);
      };
    }
  }

  /**
   * Starts listening to events
   */
  start() {
    this.stop();
    this.worker = new LiveClientWorker();

    this.worker.addEventListener('message', this.listener);
    this.worker?.postMessage({ type: 'init', socketUrl: env.APP_SOCKET_URL, debateId: this.debateId });
  }

  stop() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = undefined;
    }
  }

  setObserver(observer: SocketObserver) {
    this.observer = observer;
  }

  subscribeToDebate(debateId: string) {
    this.unsubscribeFromDebate();

    this.debateId = debateId;

    this.worker?.postMessage({
      type: 'joinDebateRoom',
      debateId: this.debateId,
    });
  }

  unsubscribeFromDebate() {
    if (this.debateId) {
      this.worker?.postMessage({
        type: 'leaveDebateRoom',
        debateId: this.debateId,
      });
      this.debateId = null;
    }
  }

  /**
   * @deprecated This function doesn't belong in the SocketService
   * @TODO find a new home or refactor this out
   */
  clear() {
    container
      .get<Structure>('structure')
      .cursor(['ui', 'sync'])
      .update('pdt', () => null);
  }

  /**
   * Handles incoming data
   */
  private handleEvent(event: WorkerEvent) {
    if (!this.observer) throw new Error('Updater not set. Cannot handle incoming live data.');

    try {
      switch (event.data.eventType) {
        case 'agenda_update':
          return this.observer.onAgendaUpdateEvent(event.data.agenda.debates);
        case 'agenda_delete':
          return this.observer.onAgendaDeleteEvent(event.data.agenda.debates);
        case 'notification':
          return this.observer.onNotificationUpdateEvent(event.data);
        case 'voting_round':
          return this.observer.onVotingRoundEvent(event.data);
        default:
          return this.observer.onDebateEvent(event.data);
      }
    } catch (error) {
      logError('SocketService', 'Failed to handle incoming event', { error });
    }
  }
}

export default SocketService;
